/**
 * @quotech/auth - Session Manager (Hardened)
 *
 * PATH: packages/auth/src/sessionManager.ts
 * PURPOSE: Unified session handling across all Quotech ERP hubs
 *
 * FEATURES:
 * - Automatic token refresh before expiration with retry logic
 * - Session activity tracking (debounced)
 * - Global logout detection (cross-tab via localStorage)
 * - Inactivity timeout (30 minutes) - skipped if "Remember me" is enabled
 * - Redirect to master login on session death
 * - Visibility-based refresh (refresh when tab becomes visible)
 * - Token sync to localStorage for API clients
 * - Network error recovery with exponential backoff
 *
 * USAGE:
 * ```typescript
 * import { SessionManager } from '@quotech/auth'
 *
 * // In App.tsx
 * useEffect(() => {
 *   const manager = new SessionManager()
 *   manager.startMonitoring()
 *   return () => manager.stopMonitoring()
 * }, [])
 * ```
 */

import { useState, useEffect, useCallback } from 'react'
import type { LogoutReason } from './cookieAuth'

// Re-export the class and all public API
export { SessionManager } from './session-manager/SessionManager'

// =============================================================================
// SINGLETON INSTANCE
// =============================================================================

import { SessionManager } from './session-manager/SessionManager'

let globalManager: SessionManager | null = null

/**
 * Get or create the global SessionManager instance
 */
export function getSessionManager(options?: {
  onSessionExpired?: (reason: LogoutReason) => void
  onTokenRefreshed?: (newToken: string) => void
}): SessionManager {
  if (!globalManager) {
    globalManager = new SessionManager(options)
  }
  return globalManager
}

/**
 * Start session monitoring (singleton)
 */
export function startSessionMonitoring(options?: {
  onSessionExpired?: (reason: LogoutReason) => void
  onTokenRefreshed?: (newToken: string) => void
}): SessionManager {
  const manager = getSessionManager(options)
  manager.startMonitoring()
  return manager
}

/**
 * Stop session monitoring (singleton)
 */
export function stopSessionMonitoring(): void {
  if (globalManager) {
    globalManager.stopMonitoring()
    globalManager = null
  }
}

// =============================================================================
// REACT HOOK
// =============================================================================

/**
 * React hook for session management
 * Automatically starts/stops monitoring on mount/unmount
 */
export function useSessionManager(options?: {
  onSessionExpired?: (reason: LogoutReason) => void
  onTokenRefreshed?: (newToken: string) => void
}): {
  isValid: boolean
  logout: (reason?: LogoutReason) => void
  forceRefresh: () => Promise<boolean>
} {
  const [isValid, setIsValid] = useState(true)

  const handleExpired = useCallback((reason: LogoutReason) => {
    setIsValid(false)
    options?.onSessionExpired?.(reason)
  }, [options])

  const logout = useCallback((reason: LogoutReason = 'logged_out') => {
    getSessionManager().logout(reason)
  }, [])

  const forceRefresh = useCallback(async () => {
    return getSessionManager().forceRefresh()
  }, [])

  useEffect(() => {
    const manager = new SessionManager({
      onSessionExpired: handleExpired,
      onTokenRefreshed: options?.onTokenRefreshed,
    })
    manager.startMonitoring()
    setIsValid(manager.isSessionValid())
    return () => manager.stopMonitoring()
  }, [handleExpired, options?.onTokenRefreshed])

  return { isValid, logout, forceRefresh }
}
